import { Button, ButtonProps, Colors, typographyStyle, Flex } from '@revenuemarket/cds';
import { Typography } from 'antd';
import { useRouter } from 'next/router';
import { css } from '~libs/emotion';

interface Props extends Omit<ButtonProps, 'onClick'> {
  icon: React.ReactNode;
  text: string;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const NavigationButton: React.FC<Props> = ({ icon, text, href, onClick, ...props }) => {
  const router = useRouter();
  const active = router.pathname === href;

  const isExternalLink = href?.startsWith('http://') || href?.startsWith('https://');

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (onClick) {
      onClick(e);
      return;
    }

    // 외부 링크인 경우 기본 동작 사용 (target="_blank"로 새 탭에서 열림)
    if (isExternalLink) {
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      return; // ctrl+click 시 기본 동작만 수행
    }

    e.preventDefault();

    if (href) {
      router.push(href);
    }
  };

  const Wrapper = href ? 'a' : 'div';

  return (
    <Wrapper
      href={href}
      onClick={handleClick}
      target={isExternalLink ? '_blank' : undefined}
      rel={isExternalLink ? 'noopener noreferrer' : undefined}
      style={{
        display: 'block',
        width: '100%',
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      <Flex
        direction="column"
        align="center"
        gap={2}
        className={css`
          &:hover {
            // icon이 svg width/height 18에서 20으로 커짐
            svg {
              width: 20px;
              height: 20px;
            }
            button {
              background-color: ${Colors.grayOpacity07};
            }
          }
          &:active {
            svg {
              width: 18px;
              height: 18px;
            }
          }
          ${
            active &&
            css`
            span {
              font-weight: 500;
              color: ${Colors.gray900};
            }
            svg {
              path {
                fill: ${Colors.gray900};
              }
            }
          `
          }
        `}
      >
        <Button
          icon={icon}
          buttonType="transparent"
          style={{
            width: 32,
            backgroundColor: active ? Colors.grayOpacity07 : '',
          }}
          {...props}
        />
        <Typography.Text
          className={css`
            ${typographyStyle['12_400']}
            color: ${Colors.gray700};
          `}
        >
          {text}
        </Typography.Text>
      </Flex>
    </Wrapper>
  );
};

export default NavigationButton;
